/**
 * The share page's pattern library. Every pattern is a list of levels, one per
 * FRAME_MS, played on repeat (the same frame length as the app's Pattern
 * Studio, and the first eight vibration shapes are the app's presets).
 *
 * Vibration levels go 0 (off) → 1 (full). Twirl levels go round a centre
 * stop: 0.5 is still, below turns clockwise, above counter-clockwise (as on
 * the app's Controller, where pulling the twirl knob down turns clockwise).
 */
export const FRAME_MS = 100;

export interface PagePattern {
  id: string;
  name: string;
  /** One line about how it feels. */
  feel: string;
  values: number[];
}

const frames = (seconds: number) => Math.max(2, Math.round((seconds * 1000) / FRAME_MS));
const clamp = (v: number) => Math.max(0, Math.min(1, v));
const wave = (n: number, cycles: number, lo = 0.15, hi = 1) =>
  Array.from({ length: n }, (_, i) => lo + (hi - lo) * (0.5 - 0.5 * Math.cos((2 * Math.PI * cycles * i) / n)));
const seeded = (seed: number) => () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
/** Many shapes joined end to end. */
const seq = (...parts: number[][]) => parts.flat();
const hold = (v: number, seconds: number) => new Array(frames(seconds)).fill(v);
const ramp = (from: number, to: number, seconds: number) => {
  const n = frames(seconds);
  return Array.from({ length: n }, (_, i) => from + (to - from) * (i / Math.max(1, n - 1)));
};

export const VIBE_PATTERNS: PagePattern[] = [
  // The app's presets.
  { id: 'wave', name: 'Wave', feel: 'Rolls up and down', values: wave(frames(4), 2) },
  { id: 'pulse', name: 'Pulse', feel: 'On, off, on, off', values: Array.from({ length: frames(2) }, (_, i) => (Math.floor(i / 3) % 2 === 0 ? 1 : 0)) },
  { id: 'ramp', name: 'Ramp up', feel: 'Gentle to full, then again', values: ramp(0.1, 1, 6) },
  {
    id: 'heartbeat',
    name: 'Heartbeat',
    feel: 'Lub-dub, rest',
    values: new Array(frames(2)).fill(0).map((_: number, i: number, a: number[]) => {
      const p = i / a.length;
      return p < 0.08 ? 1 : p < 0.16 ? 0.2 : p < 0.24 ? 0.85 : 0.1;
    }),
  },
  {
    id: 'tease',
    name: 'Tease',
    feel: 'Builds, stops just short',
    values: new Array(frames(8)).fill(0).map((_: number, i: number, a: number[]) => {
      const p = ((i / a.length) * 2) % 1;
      return p < 0.85 ? 0.1 + 0.8 * p : 0.05;
    }),
  },
  { id: 'stairs', name: 'Stairs', feel: 'Four steps up', values: new Array(frames(6)).fill(0).map((_: number, i: number, a: number[]) => 0.25 * (1 + Math.floor((i / a.length) * 4))) },
  {
    id: 'rumble',
    name: 'Rumble',
    feel: 'Restless and strong',
    values: (() => {
      const r = seeded(7);
      return Array.from({ length: frames(4) }, () => 0.45 + 0.55 * r());
    })(),
  },
  { id: 'surge', name: 'Surge', feel: 'One big swell', values: wave(frames(3), 1, 0, 1).map(v => v * v) },
  // More.
  { id: 'flutter', name: 'Flutter', feel: 'Quick light flicks', values: Array.from({ length: frames(2) }, (_, i) => (i % 2 ? 0.25 : 0.85)) },
  { id: 'tide', name: 'Tide', feel: 'Slow, deep swells', values: wave(frames(10), 1, 0.1, 0.85) },
  { id: 'knock', name: 'Knock knock', feel: 'Three knocks, a pause', values: seq(hold(1, 0.2), hold(0, 0.2), hold(1, 0.2), hold(0, 0.2), hold(1, 0.2), hold(0, 1.2)) },
  {
    id: 'quake',
    name: 'Earthquake',
    feel: 'Shakes harder and harder',
    values: (() => {
      const r = seeded(11);
      const n = frames(6);
      return Array.from({ length: n }, (_, i) => clamp((0.2 + 0.8 * (i / n)) * (0.6 + 0.4 * r())));
    })(),
  },
  { id: 'edge', name: 'Edge', feel: 'Long climb, then nothing', values: seq(ramp(0.1, 0.95, 10), hold(0, 2)) },
  { id: 'climax', name: 'Big finish', feel: 'Build, full blast, melt', values: seq(ramp(0.4, 1, 5), hold(1, 3), ramp(1, 0, 2), hold(0, 1)) },
  {
    id: 'ripple',
    name: 'Ripple',
    feel: 'A hit that fades away',
    values: new Array(frames(3)).fill(0).map((_: number, i: number, a: number[]) => {
      const p = i / a.length;
      return clamp(Math.exp(-3 * p) * (0.55 + 0.45 * Math.cos(2 * Math.PI * 6 * p)));
    }),
  },
  { id: 'steady', name: 'Steady', feel: 'Even and constant', values: hold(0.65, 1) },
];

/** Twirl at `speed` −1…1: positive is clockwise (below the centre), negative counter-clockwise. */
const turn = (speed: number) => 0.5 - speed / 2;

export const TWIRL_PATTERNS: PagePattern[] = [
  { id: 'spin', name: 'Spin', feel: 'Steady clockwise', values: hold(turn(0.75), 1) },
  { id: 'reverse', name: 'Reverse', feel: 'Steady counter-clockwise', values: hold(turn(-0.75), 1) },
  { id: 'swing', name: 'Swing', feel: 'Smoothly back and forth', values: new Array(frames(3)).fill(0).map((_: number, i: number, a: number[]) => turn(0.8 * Math.sin((2 * Math.PI * i) / a.length))) },
  { id: 'pendulum', name: 'Pendulum', feel: 'Forward, pause, back, pause', values: seq(hold(turn(0.8), 1.5), hold(0.5, 0.5), hold(turn(-0.8), 1.5), hold(0.5, 0.5)) },
  { id: 'windup', name: 'Wind up', feel: 'Faster and faster, then stop', values: seq(ramp(turn(0.1), turn(1), 5), hold(0.5, 1)) },
  { id: 'whirl', name: 'Whirl', feel: 'Fast flips of direction', values: seq(hold(turn(1), 0.6), hold(turn(-1), 0.6)) },
  { id: 'corkscrew', name: 'Corkscrew', feel: 'Clockwise bursts', values: seq(hold(turn(0.9), 0.4), hold(0.5, 0.4)) },
  { id: 'teasespin', name: 'Tease spin', feel: 'Lazy turns, then a rush', values: seq(hold(turn(0.25), 3), hold(turn(1), 1)) },
  { id: 'wobble', name: 'Wobble', feel: 'Tiny quick twists', values: Array.from({ length: frames(1.6) }, (_, i) => turn(Math.floor(i / 2) % 2 ? -0.35 : 0.35)) },
  { id: 'drift', name: 'Drift', feel: 'Slow, wandering turns', values: new Array(frames(8)).fill(0).map((_: number, i: number, a: number[]) => turn(0.9 * Math.sin((2 * Math.PI * i) / a.length))) },
];
